import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PAGE, FinancingLoanType } from '@/constants/common';
import {
  LEGAL_FEE,
  MORTGAGE_INTEREST_RATE,
  DEFAULT_LOAN_DETAILS_AGE_65,
  FOUNDATION_COMPLETION_PERIOD,
  LOAN_TYPE,
  NumberOfExistingLoans,
} from '@/features/private-resale/purchase-new-project/constants';
import { FormTypes } from '.';
import moment from 'moment';
import { UseFinancingVariablesReturn } from './hooks/useFinancingVariables';
import { UsePotentialExpensesReturn } from './hooks/usePotentialExpenses';
import { UsePurchaseOutlayBreakdownReturn } from './hooks/usePurchaseOutlayBreakdown';
import { UseProgressivePaymentReturn } from './hooks/useProgressivePayment';

interface PurchaseNewProjectState {
  formField: FormTypes &
    Partial<{
      financingSection: UseFinancingVariablesReturn;
      potentialExpensesSection: UsePotentialExpensesReturn;
      purchaseOutlayBreakdownSection: UsePurchaseOutlayBreakdownReturn;
      progressivePaymentSection: UseProgressivePaymentReturn;
    }>;
  loading: boolean;
  from: PAGE | '';
}

export const initialState: PurchaseNewProjectState = {
  formField: {
    // Purchase Details
    purchasePrice: 0,

    // Potential Expenses
    bsd: 0,
    absd: 0,
    legalFees: LEGAL_FEE.DEFAULT,

    // Housing Loan
    loanType: LOAN_TYPE.BANK as 'bank' | 'no-loan',
    numberOfExistingLoans: NumberOfExistingLoans.NoLoan,

    // Financing Variables
    financingVariables: {
      financingLoanType: FinancingLoanType.sixtyFiveYearLoan,
      loanTenure: DEFAULT_LOAN_DETAILS_AGE_65.LOAN_TENURE,
      mortgageInterestRate: MORTGAGE_INTEREST_RATE.DEFAULT,
      loanToValue: DEFAULT_LOAN_DETAILS_AGE_65.LOAN_TO_VALUE_DEFAULT,
    },

    // Breakdown
    isCheckedMonthlyPayable: true,

    // Timeline
    otpDate: moment().format('YYYY-MM-DD'),
    completionFoundationPeriod: FOUNDATION_COMPLETION_PERIOD.EIGHT_WEEKS,
  },
  loading: true,
  from: '' as PAGE | '',
};

export const purchaseNewProjectSlice = createSlice({
  name: 'purchaseNewProject',
  initialState,
  reducers: {
    updateDataForm: (state, action: PayloadAction<FormTypes & { from: PAGE | '' }>) => {
      const { from, ...rest } = action.payload;

      state.formField = rest;
      state.from = action.payload.from;
    },
    updateFrom: (state, action: PayloadAction<PAGE>) => {
      state.from = action.payload;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: () => {},
});

export const { updateDataForm, updateFrom, reset } = purchaseNewProjectSlice.actions;

export default purchaseNewProjectSlice.reducer;
